// TODO: go over file and check what functions are custom
// TODO: add function top comments

import { isEmpty } from '@/helpers/is';
import { stringToDate } from './date';

const noop = () => null;

/**
 * validate is value not empty
 * @param {string} val
 * @returns {boolean}
 */
const required = (val) => !isEmpty(val);

/**
 * validate only numbers
 * @param {string} val
 * @returns {boolean}
 */
const numbers = (val) => /^[0-9]+$/.test(val);

/**
 * validate only numbers
 * @param {string} val
 * @returns {boolean}
 */
const decimalNumber = (val) => /^\d+(\.\d+)?$/.test(String(val));

/**
 * Validate percentage value (0 to 100, including decimals)
 * @param {string|number} val
 * @returns {boolean}
 */
const percentage = (val) => {
  const num = parseFloat(val);
  return /^\d+(\.\d+)?$/.test(String(val)) && num >= 0 && num <= 100;
};

/**
 * validate only english characters
 * @param {string} val
 * @returns {boolean}
 */
const english = (val) => /[a-zA-Z]/.test(val);

/**
 * Validate only Hebrew characters and spaces
 * @param {string} val
 * @returns {boolean}
 */
const hebrew = (val) => /^[\u0590-\u05FF\s]+$/.test(val);

/**
 * Validate minimum length of string
 * @param {string} val
 * @param {number} min - minimum length required
 * @returns {boolean}
 */
const minLength = (val, min = 2) => val.length >= min;

/**
 * validate only hebrew characters with special characters
 * @param {string} val
 * @returns {boolean}
 */
const hebrewWithCharacters = (val) => /^[\u05D0-\u05EA׳\s-]+$/.test(val);

/**
 * validate only hebrew characters with special characters and digits
 * @param {string} val
 * @returns {boolean}
 */
const hebrewWithDigits = (val) => /^[\u05D0-\u05EA׳\s-\d]+$/.test(val);

/**
 * validate only characters
 * @param {string} val
 * @returns {boolean}
 */
const characters = (val) => english(val) || hebrew(val);

/**
 * validate israel mobile phone number
 * @param {string} val
 * @returns {boolean}
 */
const mobile = (val) => /^05[0-9]{8}$/.test(val);

/**
 * check if variable is phone number
 * @param {string} val
 * @returns {boolean}
 */
const phone = (val) => /^(?:0(([23489]\d{7})|(5[0-9]\d{7})|(77\d{7})))$/.test(val);

/**
 * validate email address
 * @param {string} val
 * @returns {boolean}
 */
const email = (val) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val);

/**
 * Validate if the input is a valid zip code.
 * @param {string} val - The string to validate.
 * @returns {boolean} True if the input is a numeric string with 5 to 7 digits.
 */
const zipCode = (val) => /^\d{5,7}$/.test(val);

/**
 * Validate if the input is avalid case number.
 * @param {string} val - The string to validate.
 * @returns {boolean} True if the input is a numeric string with 5 to 7 digits.
 */
const caseNumber = (val) => /^\d{5,7}$/.test(val);

/**
 * validate year
 * @param {string} val
 * @returns {boolean}
 */
const year = (val) => /^[12]\d{3}$/.test(val);

/**
 * Validate time
 * @param {string} val
 * @returns {boolean}
 */
const time = (val) => /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(val);

/**
 * Validate policy number
 * @param {string} val
 * @returns {boolean}
 */
const policy = (val) => /^\d{12}$/.test(val);

/**
 * Validate date in DD/MM/YYYY format and ensure it's not in the future
 * @param {string} val
 * @returns {boolean}
 */
const date = (val) => {
  const regex = /^(0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
  if (!regex.test(val)) {
    return false;
  }
  return true;
};

/**
 * Check if the date is not in the future
 * @param {string} val
 * @returns {boolean}
 */
const isNotFutureDate = (val) => {
  const inputDate = stringToDate(val);
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  return inputDate <= currentDate;
};

/**
 * validate plate number
 * @param {string} val
 * @returns {boolean}
 */
const plateNumber = (val) => /^\d{7,8}$/.test(val);

/**
 * validate israel id number
 * @param {string | number} val
 * @returns {boolean}
 */
const uid = (val) => {
  let id = String(val).trim();
  id = id.length < 9 ? (`00000000${id}`).slice(-9) : id;
  if (id.length > 9 || Number.isNaN(id)) return false;
  return Array.from(id, Number).reduce((counter, digit, i) => {
    const step = digit * ((i % 2) + 1);
    return counter + (step > 9 ? step - 9 : step);
  }) % 10 === 0;
};
const hebrewWithPunctuation = (val) => /^[\u05D0-\u05EA׳\s\d.,;?!:()"'-]+$/.test(val);

const isValidYear = (val) => {
  const inputYear = parseInt(val, 10);
  const currentYear = new Date().getFullYear();
  return inputYear <= currentYear;
};

const maxPastYears = (val) => {
  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 100;

  return +val >= minYear;
};

export const isAtLeast18YearsOld = (dateItem) => {
  if (!dateItem) return false;
  const dateParts = dateItem.split('/');
  const birthDate = new Date(`${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`);

  const today = new Date();
  const minAllowedDate = new Date(today);
  minAllowedDate.setFullYear(today.getFullYear() - 18);
  return birthDate <= minAllowedDate;
};

const validateNumberWithOneDecimal = (val) => {
  const regex = /^[0-9]+(\.[0-9]{1,2})?$/; // Matches only numbers and one decimal point with one or two digits
  return regex.test(val);
};
const isValidNumber = (value) => parseFloat(value) <= 100;

const isValidNumberFormat = (value) => {
  const regex = /^\d{1,2}(\.\d{1,2})?$/;
  return regex.test(value);
};

const password = (value) => value.length >= 6 && /[0-9]/.test(value);

const validations = {
  required,
  mobile,
  email,
  uid,
  numbers,
  decimalNumber,
  percentage,
  english,
  hebrew,
  minLength,
  characters,
  hebrewWithCharacters,
  hebrewWithDigits,
  year,
  plateNumber,
  phone,
  time,
  date,
  zipCode,
  caseNumber,
  hebrewWithPunctuation,
  policy,
  isNotFutureDate,
  isValidYear,
  maxPastYears,
  isAtLeast18YearsOld,
  validateNumberWithOneDecimal,
  isValidNumber,
  isValidNumberFormat,
  password,
};

/**
 * wrapper function for mantine form validation
 * @param {mixed} value
 * @param {object} data
 * @returns {string | null}
 */
const validate = (value, data = {}) => {
  let output = null;
  const formattedValue = (value ?? '').toString().trim();
  Object.keys(data).forEach((key) => {
    const error = data[key];
    const func = validations[key] || noop;

    // continue in case we already have output
    if (output) {
      return;
    }
    output = func(formattedValue) ? null : error;
  });
  return output;
};

export default validate;
export {
  numbers,
  english,
  hebrew,
  characters,
  percentage,
};
